import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight, CheckCircle, Users, Settings, Heart,
  Sparkles, TrendingUp, Handshake, Target, Layers, Award,
  ClipboardCheck, FileText, BarChart3,
  Zap, Building2, BookOpen, Monitor,
} from "lucide-react";
import SEO from "../components/SEO";
import { useRef, useState, useEffect } from "react";

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const Reveal = ({ children, className = "", delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDuration: "800ms", transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const WaveDivider = ({ fill = "#F7F7F7" }) => (
  <div className="relative -mb-1">
    <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
      <path d="M0 100V40C180 70 360 10 540 30C720 50 900 90 1080 70C1260 50 1350 20 1440 40V100H0Z" fill={fill} />
    </svg>
  </div>
);

const EhrPartnership = () => {
  const customerQuestions = [
    "Scheduling", "Patient intake", "Documentation", "Billing",
    "Staff workflows", "Role responsibilities", "Reporting", "Using platform features consistently",
  ];

  const teamBenefits = [
    { icon: Users, title: "Less Pressure on Your Internal Team", description: "Give your implementation and customer success teams additional capacity without immediately adding headcount." },
    { icon: Heart, title: "A More Hands-On Customer Experience", description: "Offer customers support that goes beyond virtual training and standard documentation." },
    { icon: Zap, title: "Faster Customer Confidence", description: "Help staff work through real-world questions while they are actually using the platform." },
    { icon: Settings, title: "Better Workflow Adoption", description: "Reinforce the workflows and features that matter most to each practice." },
    { icon: TrendingUp, title: "More Scalable Customer Support", description: "Create an additional support option that can grow with your customer base." },
  ];

  const growingSignals = [
    "Growing your customer base quickly",
    "Expanding into new practice types or specialties",
    "Supporting customers with a lean internal team",
    "Looking for ways to differentiate your implementation experience",
    "Not ready to build a large customer success organization",
    "Looking for a more hands-on option for customers who need additional support",
  ];

  const preferredFitList = [
    "Startup and growing practices",
    "First-time EHR users",
    "Smaller teams that need additional support",
    "Multi-provider implementations",
    "More operationally complex practices",
    "Customers who need extra reinforcement after training",
  ];

  const pilotMetrics = [
    "Customer feedback", "Staff confidence", "Workflow consistency",
    "Support questions and recurring issues", "Use of key platform features",
    "Time to successful workflow adoption", "Areas where customers need additional reinforcement",
  ];

  const engagementSteps = [
    { num: "1", title: "Platform & Workflow Orientation", description: "We learn your platform, training materials, preferred workflows, and implementation standards." },
    { num: "2", title: "Practice Workflow Review", description: "We help the customer connect your platform to the way their practice actually operates." },
    { num: "3", title: "Hands-On Customer Support", description: "We work directly with providers and staff as they begin incorporating the platform into their daily workflows." },
    { num: "4", title: "Workflow Reinforcement", description: "We identify recurring questions and areas where additional guidance may be needed." },
    { num: "5", title: "Ongoing Support", description: "Depending on the engagement, we can provide virtual follow-up, office hours, workflow refinement, and additional staff support." },
    { num: "6", title: "Feedback to Your Team", description: "We can provide insights into common customer questions, workflow challenges, and areas where additional training or product education may be helpful." },
  ];

  const supportCapabilities = [
    { icon: Users, title: "Role-Based Workflow Support", description: "Support for providers, front desk staff, billing teams, and administrators based on how they use the platform." },
    { icon: FileText, title: "Workflow Documentation", description: "Clear SOPs for core processes such as scheduling, intake, documentation, billing, and reporting." },
    { icon: ClipboardCheck, title: "Billing & Administrative QA", description: "Support identifying common workflow and data entry issues that can create problems downstream." },
    { icon: BookOpen, title: "Staff Reinforcement", description: "Additional support for teams that need more practice after completing standard platform training." },
    { icon: BarChart3, title: "Adoption Insights", description: "Observations around where customers are using the platform effectively and where they may need additional support." },
    { icon: Award, title: "Customer Outcomes Brief", description: "A concise summary of implementation observations, adoption challenges, and opportunities for continued improvement." },
  ];

  return (
    <div className="overflow-hidden" style={{ fontFamily: "'DM Sans', 'Public Sans', system-ui, sans-serif" }}>
      <SEO
        title="EHR Partnership | Implementation & Adoption Support"
        description="RNB Consulting Group partners with growing EHR companies to provide hands-on implementation, workflow, and adoption support for healthcare practices."
        keywords="EHR implementation support, EHR adoption, healthcare technology partner, EHR customer success, practice workflow support"
      />

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-[85vh] sm:min-h-[88vh] flex items-center overflow-hidden bg-[#0B1F3A]">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/eytwj5rx_GettyImages-1383959854-e1740018165147.jpg"
            alt="Healthcare implementation support"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A]/95 via-[#0B1F3A]/85 to-[#00838A]/30" />
        </div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-[10%] w-64 sm:w-80 h-64 sm:h-80 bg-[#C9A84C]/10 rounded-full blur-[100px] animate-float" />
          <div className="absolute bottom-32 left-[5%] w-72 sm:w-96 h-72 sm:h-96 bg-[#00838A]/15 rounded-full blur-[120px] animate-float" style={{ animationDelay: "3s" }} />
        </div>

        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 pb-28 relative z-10 w-full text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 mb-6 sm:mb-8">
              <Handshake className="w-4 h-4 text-[#C9A84C]" />
              <span className="text-xs sm:text-sm font-medium text-white/90">EHR Vendor Partnership</span>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-white mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="ehr-partnership-headline"
            >
              Your EHR Is Ready.{" "}
              <span className="block sm:inline text-[#C9A84C]">Give Your Customers the Support to Make It Work.</span>
            </h1>
          </Reveal>

          <Reveal delay={200}>
            <p className="text-sm sm:text-base lg:text-lg text-white/75 leading-relaxed mb-8 sm:mb-10 max-w-2xl mx-auto">
              Hands-on implementation and adoption support for growing EHR companies that want to deliver a better customer experience without expanding their internal team.
            </p>
          </Reveal>

          <Reveal delay={300}>
            <div className="sm:inline-block">
              <Link to="/consultation" className="block">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-[#00838A] hover:bg-[#00838A]/90 text-white rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-semibold shadow-lg shadow-[#00838A]/25 hover:-translate-y-0.5 transition-all duration-300"
                  data-testid="hero-cta"
                >
                  Talk With Us <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10">
          <WaveDivider fill="#FFFFFF" />
        </div>
      </section>

      {/* ═══ INTRO ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-sm sm:text-base lg:text-lg text-[#5A5A5A] leading-relaxed mb-5">
              You built the platform. Your team knows it inside and out.
            </p>
          </Reveal>
          <Reveal delay={50}>
            <p className="text-sm sm:text-base lg:text-lg text-[#5A5A5A] leading-relaxed mb-5">
              But your customers still need help figuring out how to make it work in their actual practice.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-base sm:text-lg font-semibold text-[#00838A] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              That is where we come in.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed mb-5">
              RNB Consulting Group partners with growing EHR companies to provide hands-on implementation, workflow, and adoption support for healthcare practices.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
              We step in where virtual training and standard onboarding end, helping your customers turn what they learned into workflows they can actually use.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ PLATFORM TO PRACTICE ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-6 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              You Built the Platform. We Help Your Customers Put It Into Practice.
            </h2>
          </Reveal>

          <Reveal delay={50}>
            <div className="max-w-3xl mx-auto space-y-4 text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
              <p>Your team has a lot on its plate.</p>
              <p>You are implementing new customers, answering support questions, providing training, managing product feedback, and continuing to build and improve your platform.</p>
              <p>As you grow, providing every customer with the same level of hands-on support becomes harder.</p>
              <p className="font-medium text-[#0B1F3A]">And sometimes, the issue is not really the software.</p>
              <p>The customer understands the platform. They just need help figuring out how it fits into the way their practice actually operates.</p>
              <p>That can mean questions about:</p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-6 mb-6 max-w-3xl mx-auto">
              {customerQuestions.map((q) => (
                <div key={q} className="flex items-center gap-2 bg-white p-3 rounded-xl shadow-[0_2px_8px_rgba(11,31,58,0.04)]">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00838A] shrink-0" />
                  <span className="text-xs sm:text-sm text-[#5A5A5A]">{q}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="max-w-3xl mx-auto space-y-4 text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
              <p>These are the moments when customers need someone who understands both the technology and the realities of healthcare operations.</p>
              <p className="font-semibold text-[#00838A]" style={{ fontFamily: "'Playfair Display', serif" }}>That is where RNB can extend your team.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ MORE SUPPORT WITHOUT BIGGER TEAM ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Give Your Customers More Support Without Building a Bigger Team.
            </h2>
          </Reveal>
          <Reveal delay={50}>
            <div className="space-y-4 text-sm sm:text-base text-[#5A5A5A] leading-relaxed text-left sm:text-center">
              <p>You do not need to hire another implementation specialist just to provide additional hands-on support.</p>
              <p>You do not need to build a new customer success function before you are ready.</p>
              <p>And you should not have to choose between giving customers a high-touch experience and keeping your internal team focused on growth.</p>
              <p>RNB provides an additional layer of customer support that can fit into your existing implementation and onboarding process.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="mt-8 bg-[#E8F4F5] rounded-2xl p-6 sm:p-8">
              <p className="text-sm sm:text-base font-medium text-[#0B1F3A]">Your team focuses on the platform.</p>
              <p className="text-base sm:text-lg font-bold text-[#00838A] mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>We help your customers make it work in practice.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ EXTENSION OF YOUR TEAM ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F4F5] text-[#00838A] text-xs sm:text-sm font-semibold mb-4">
                <Handshake className="w-4 h-4" /> Our Approach
              </span>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Think of Us as an Extension of Your Team.
              </h2>
            </div>
          </Reveal>

          <Reveal delay={50}>
            <div className="max-w-3xl mx-auto text-sm sm:text-base text-[#5A5A5A] leading-relaxed text-center mb-10 sm:mb-14 space-y-4">
              <p>We learn your platform, your preferred workflows, and your implementation standards so we can support customers in a way that aligns with your process.</p>
              <p>Depending on the engagement, we can support customers before, during, and after implementation.</p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {[
              { title: "Before Implementation", description: "We help validate practice workflows and prepare staff for how the platform will fit into their day-to-day operations.", icon: Target },
              { title: "During Implementation", description: "We provide hands-on support as staff begin using the platform in their actual work environment. Questions can be addressed in the moment instead of becoming another support ticket.", icon: Monitor },
              { title: "After Implementation", description: "We reinforce workflows, identify areas where staff are struggling, and help practices build confidence and consistency with the platform.", icon: TrendingUp },
            ].map((phase, idx) => (
              <Reveal key={phase.title} delay={idx * 100}>
                <Card className="group border-0 bg-white shadow-[0_4px_20px_rgba(11,31,58,0.05)] hover:shadow-[0_12px_40px_rgba(0,131,138,0.1)] rounded-2xl transition-all duration-500 hover:-translate-y-1 h-full">
                  <CardContent className="p-5 sm:p-6 lg:p-7">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#E8F4F5] flex items-center justify-center mb-4 group-hover:bg-[#00838A]/15 transition-colors duration-300">
                      <phase.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#00838A]" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#0B1F3A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{phase.title}</h3>
                    <p className="text-xs sm:text-sm text-[#5A5A5A] leading-relaxed">{phase.description}</p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>

          <Reveal delay={300}>
            <div className="text-center mt-10">
              <p className="text-base sm:text-lg font-bold text-[#0B1F3A]" style={{ fontFamily: "'Playfair Display', serif" }}>The goal is simple:</p>
              <p className="text-sm sm:text-base text-[#5A5A5A] mt-2 max-w-2xl mx-auto">Help your customers become comfortable using your platform so your team can spend less time putting out fires and more time growing the business.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ WHAT THIS MEANS FOR YOUR TEAM ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-[#0B1F3A] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-[15%] w-64 sm:w-80 h-64 sm:h-80 bg-[#00838A]/12 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-[10%] w-56 sm:w-72 h-56 sm:h-72 bg-[#C9A84C]/8 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-10 sm:mb-14 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              What This Means for Your Team
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {teamBenefits.map((item, idx) => (
              <Reveal key={item.title} delay={idx * 80}>
                <div className={`p-5 sm:p-6 bg-white/[0.07] rounded-xl border border-white/10 backdrop-blur-sm hover:bg-white/[0.12] transition-all duration-300 h-full ${idx === 4 ? "sm:col-span-2 lg:col-span-1" : ""}`}>
                  <item.icon className="w-6 h-6 text-[#C9A84C] mb-3" />
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                  <p className="text-xs sm:text-sm text-white/70 leading-relaxed">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider fill="#E8F4F5" />

      {/* ═══ BUILT FOR GROWING EHR COMPANIES ═══ */}
      <section className="pt-14 sm:pt-20 pb-16 sm:pb-24 bg-[#E8F4F5]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-5">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00838A]/10 text-[#00838A] text-xs sm:text-sm font-semibold mb-4">
                <Building2 className="w-4 h-4" /> Who We Work With
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Built for Growing EHR Companies
              </h2>
            </div>
          </Reveal>
          <Reveal delay={50}>
            <p className="text-center text-sm sm:text-base text-[#5A5A5A] max-w-2xl mx-auto mb-8 leading-relaxed">
              Our model is designed with smaller and emerging EHR companies in mind. You may be:
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-3xl mx-auto mb-8">
              {growingSignals.map((item) => (
                <div key={item} className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-[0_2px_8px_rgba(11,31,58,0.04)]">
                  <CheckCircle className="w-4 h-4 text-[#00838A] shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-[#5A5A5A]">{item}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="text-center space-y-3">
              <p className="text-sm sm:text-base font-medium text-[#0B1F3A]">You do not have to build everything internally.</p>
              <p className="text-sm sm:text-base text-[#5A5A5A]">RNB can provide the additional operational support that helps you deliver a stronger customer experience as you grow.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <WaveDivider fill="#FFFFFF" />

      {/* ═══ PARTNERSHIP OPTIONS ═══ */}
      <section className="pt-14 sm:pt-20 pb-16 sm:pb-24 bg-white" id="partnership-options">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-10 sm:mb-14 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
              Choose the Partnership That Fits Your Business
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-7 max-w-5xl mx-auto">
            {/* Preferred Partner */}
            <Reveal delay={0}>
              <Card className="group border-0 bg-white shadow-[0_4px_24px_rgba(11,31,58,0.06)] hover:shadow-[0_12px_40px_rgba(0,131,138,0.12)] rounded-2xl transition-all duration-500 hover:-translate-y-2 h-full relative overflow-hidden">
                <div className="h-1 bg-[#00838A]" />
                <CardContent className="p-5 sm:p-7">
                  <div className="w-11 h-11 rounded-xl bg-[#E8F4F5] flex items-center justify-center mb-4">
                    <Handshake className="w-5 h-5 text-[#00838A]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0B1F3A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Preferred Implementation & Adoption Partner</h3>
                  <p className="text-xs sm:text-sm text-[#00838A] font-medium mb-4">Give customers access to additional hands-on support when they need it.</p>
                  <div className="space-y-3 text-xs sm:text-sm text-[#5A5A5A] leading-relaxed mb-5">
                    <p>When a customer would benefit from additional implementation or workflow support, your team can recommend RNB as a preferred partner.</p>
                    <p>The customer works directly with RNB at a preferred partner rate.</p>
                    <p>We provide the additional hands-on support while keeping your platform, workflows, and implementation standards at the center of the engagement.</p>
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#0B1F3A]/40 mb-3">This is a great fit for:</p>
                  <div className="space-y-2 mb-5">
                    {preferredFitList.map((item) => (
                      <div key={item} className="flex items-start gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-[#00838A] shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm text-[#5A5A5A]">{item}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-[#0B1F3A]">You expand your customer experience without expanding your internal team.</p>
                </CardContent>
              </Card>
            </Reveal>

            {/* White-Label */}
            <Reveal delay={120}>
              <Card className="group border-0 bg-white shadow-[0_4px_24px_rgba(11,31,58,0.06)] hover:shadow-[0_12px_40px_rgba(0,131,138,0.12)] rounded-2xl transition-all duration-500 hover:-translate-y-2 h-full relative overflow-hidden">
                <div className="h-1 bg-[#C9A84C]" />
                <CardContent className="p-5 sm:p-7">
                  <div className="w-11 h-11 rounded-xl bg-[#0B1F3A]/5 flex items-center justify-center mb-4">
                    <Layers className="w-5 h-5 text-[#C9A84C]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0B1F3A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>White-Label Customer Support</h3>
                  <p className="text-xs sm:text-sm text-[#C9A84C] font-medium mb-4">Give customers a more integrated experience with RNB operating as an extension of your team.</p>
                  <div className="space-y-3 text-xs sm:text-sm text-[#5A5A5A] leading-relaxed mb-5">
                    <p>For EHR companies that want a more seamless model, RNB can provide support behind the scenes as an extension of your implementation or customer success team.</p>
                    <p>Your customers get the hands-on support they need.</p>
                    <p>Your team gets additional capacity.</p>
                    <p>And you can offer a more robust customer experience without having to immediately build another internal service line.</p>
                  </div>
                  <p className="text-xs sm:text-sm italic text-[#00838A] font-medium">Commercial structure is determined collaboratively.</p>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ PILOT ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-[#0B1F3A] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-[15%] w-64 sm:w-80 h-64 sm:h-80 bg-[#00838A]/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-[10%] w-56 sm:w-72 h-56 sm:h-72 bg-[#C9A84C]/10 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <div className="text-center mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 mb-6">
                <Target className="w-4 h-4 text-[#C9A84C]" />
                <span className="text-xs sm:text-sm font-medium text-white/90">Start Small</span>
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Not Sure If This Is Right for Your Customers?
              </h2>
            </div>
          </Reveal>
          <Reveal delay={50}>
            <div className="max-w-2xl mx-auto text-center space-y-4 text-sm sm:text-base text-white/70 leading-relaxed mb-8">
              <p className="text-base sm:text-lg font-semibold text-[#C9A84C]" style={{ fontFamily: "'Playfair Display', serif" }}>Start Small.</p>
              <p>You do not have to commit to a long-term partnership to see how this could work.</p>
              <p>Choose 3 to 5 upcoming customer implementations where additional hands-on support could make a difference.</p>
              <p>We will work with your team to support those customers and evaluate the results.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="max-w-2xl mx-auto">
              <p className="text-white/80 text-sm sm:text-base mb-4 text-center">Together, we can look at:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {pilotMetrics.map((item) => (
                  <div key={item} className="flex items-start gap-2 p-3 bg-white/[0.07] rounded-xl border border-white/10">
                    <CheckCircle className="w-4 h-4 text-[#C9A84C] shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="text-center space-y-3 mb-8">
              <p className="text-sm sm:text-base text-white/60">At the end of the pilot, you will have real customer experience and data to help determine whether RNB should become part of your broader implementation and support strategy.</p>
              <p className="text-sm text-white/50 italic">No long-term commitment. Just a chance to see what additional support could do for your customers and your team.</p>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="text-center">
              <div className="sm:inline-block">
                <Link to="/consultation" className="block">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-[#C9A84C] text-[#0B1F3A] hover:bg-[#C9A84C]/90 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-bold shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                    data-testid="pilot-cta"
                  >
                    Talk to Us About a Pilot <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <WaveDivider fill="#F7F7F7" />

      {/* ═══ WHAT DOES WORKING WITH RNB LOOK LIKE? ═══ */}
      <section className="pt-14 sm:pt-20 pb-16 sm:pb-24 bg-[#F7F7F7]">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-5">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F4F5] text-[#00838A] text-xs sm:text-sm font-semibold mb-4">
                <Sparkles className="w-4 h-4" /> How It Works
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                What Does Working With RNB Look Like?
              </h2>
              <p className="text-sm sm:text-base text-[#5A5A5A] max-w-2xl mx-auto">
                Every partnership is customized around your platform, customer base, and existing implementation process. A typical engagement may include:
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mt-10">
            {engagementSteps.map((step, idx) => (
              <Reveal key={step.title} delay={idx * 80}>
                <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-[0_4px_16px_rgba(11,31,58,0.04)] hover:shadow-[0_8px_30px_rgba(0,131,138,0.1)] hover:-translate-y-1 transition-all duration-400 h-full">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0B1F3A] to-[#00838A] text-white font-bold text-lg flex items-center justify-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{step.num}</div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0B1F3A] mb-2">{step.title}</h3>
                  <p className="text-xs sm:text-sm text-[#5A5A5A] leading-relaxed">{step.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHAT WE CAN SUPPORT ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                What We Can Support
              </h2>
              <p className="text-sm sm:text-base text-[#5A5A5A] max-w-2xl mx-auto">
                Depending on the needs of your customer, our team can provide:
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {supportCapabilities.map((item, idx) => (
              <Reveal key={item.title} delay={idx * 80}>
                <Card className="group border-0 bg-[#F7F7F7] hover:shadow-[0_12px_40px_rgba(0,131,138,0.1)] rounded-2xl transition-all duration-500 hover:-translate-y-1 h-full">
                  <CardContent className="p-5 sm:p-6">
                    <div className="w-11 h-11 rounded-xl bg-[#E8F4F5] flex items-center justify-center mb-4 group-hover:bg-[#00838A]/15 transition-colors duration-300">
                      <item.icon className="w-5 h-5 text-[#00838A]" />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-[#0B1F3A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                    <p className="text-xs sm:text-sm text-[#5A5A5A] leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#0B1F3A] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-[15%] w-64 sm:w-80 h-64 sm:h-80 bg-[#00838A]/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-[10%] w-56 sm:w-72 h-56 sm:h-72 bg-[#C9A84C]/10 rounded-full blur-[100px]" />
        </div>

        <Reveal>
          <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-5 sm:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Customers Should Not Have to Figure It Out Alone.
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-white/70 leading-relaxed mb-5">
              <p>A successful implementation is more than getting the software configured and completing training.</p>
              <p>It is when the practice can actually use the platform confidently in the way it was designed to be used.</p>
              <p className="font-semibold text-[#C9A84C]">That is where RNB comes in.</p>
            </div>
            <div className="space-y-4 text-sm sm:text-base text-white/60 leading-relaxed mb-8 sm:mb-10">
              <p>We help growing EHR companies bridge the gap between learning the platform and using it successfully in the real world.</p>
              <p>You keep your focus on your product, your customers, and your growth.</p>
              <p>We help make sure your customers have the support they need along the way.</p>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Let{'\u2019'}s Talk About Your Customer Experience.
            </h3>
            <p className="text-sm text-white/50 mb-8 max-w-xl mx-auto">
              Tell us a little about your platform, your customer base, and where you are seeing the biggest implementation or adoption challenges. We will talk through where RNB could fit into your existing process and whether a partnership or pilot makes sense.
            </p>
            <div className="sm:inline-block">
              <Link to="/consultation" className="block">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-[#C9A84C] text-[#0B1F3A] hover:bg-[#C9A84C]/90 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-bold shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                  data-testid="final-cta"
                >
                  Start the Conversation <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </Link>
            </div>
            <p className="text-xs text-white/30 mt-8">
              RNB Consulting Group &middot; Atlanta, GA &middot; rnbconsultinggroup.com
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default EhrPartnership;
